export interface TaxIdType {
  id: string // e.g. 'br_cpf'
  country: string // ISO 3166-1 alpha-2, e.g. 'BR'
  label: string // human label, e.g. 'CPF'
  format?: string // optional RegExp source string for non-checksum types
  example: string
  /** true iff this tax id type identifies a natural person (vs. a legal entity). */
  individual: boolean
  /** optional human-facing input mask pattern, e.g. '000.000.000-00' */
  mask?: string
}

export const TAX_ID_TYPES: TaxIdType[] = [
  {
    id: 'br_cpf',
    country: 'BR',
    label: 'CPF',
    example: '123.456.789-09',
    individual: true,
    mask: '000.000.000-00',
  },
  {
    id: 'br_cnpj',
    country: 'BR',
    label: 'CNPJ',
    example: '11.222.333/0001-81',
    individual: false,
  },
  {
    id: 'us_ein',
    country: 'US',
    label: 'EIN',
    format: '^\\d{2}-?\\d{7}$',
    example: '12-3456789',
    individual: false,
  },
  {
    id: 'eu_vat',
    country: 'EU',
    label: 'EU VAT',
    format: '^[A-Z]{2}[A-Z0-9]{2,12}$',
    example: 'DE123456789',
    individual: false,
  },
  {
    id: 'gb_vat',
    country: 'GB',
    label: 'GB VAT',
    format: '^GB\\d{9}$',
    example: 'GB123456789',
    individual: false,
  },
  {
    id: 'passport',
    country: 'INTL',
    label: 'Passport',
    individual: true,
    example: 'AB1234567',
    format: '^[A-Z0-9]{5,12}$',
  },
]

export function taxIdCountries(): string[] {
  const seen = new Set<string>()
  const countries: string[] = []
  for (const type of TAX_ID_TYPES) {
    if (!seen.has(type.country)) {
      seen.add(type.country)
      countries.push(type.country)
    }
  }
  return countries
}

export function taxIdTypesForCountry(country: string): TaxIdType[] {
  return TAX_ID_TYPES.filter((type) => type.country === country)
}

export function individualTaxIdTypesForCountry(country: string): TaxIdType[] {
  return TAX_ID_TYPES.filter((type) => type.country === country && type.individual === true)
}

export function isIndividualTaxIdType(id: string): boolean {
  const def = TAX_ID_TYPES.find((type) => type.id === id)
  return def?.individual === true
}

const CHECKSUM_TYPE_IDS = new Set(['br_cpf', 'br_cnpj', 'us_ein'])
const UPPERCASE_TYPE_IDS = new Set(['eu_vat', 'gb_vat', 'passport'])

export function normalizeTaxId(type: string, value: string): string {
  const stripped = value.replace(/[\s.\-/]/g, '')
  if (UPPERCASE_TYPE_IDS.has(type)) {
    return stripped.toUpperCase()
  }
  if (CHECKSUM_TYPE_IDS.has(type)) {
    return stripped.replace(/\D/g, '')
  }
  return stripped
}

export interface TaxIdValidation {
  ok: boolean
  normalized?: string
  reason?: string
}

function calcModulo11Dv(digits: number[], weights: number[]): number {
  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    sum += digits[i]! * weights[i]!
  }
  const remainder = sum % 11
  return remainder < 2 ? 0 : 11 - remainder
}

function isValidCpf(digits: string): boolean {
  if (/^(\d)\1{10}$/.test(digits)) return false
  const nums = digits.split('').map(Number)
  const base = nums.slice(0, 9)
  const dv1 = calcModulo11Dv(base, [10, 9, 8, 7, 6, 5, 4, 3, 2])
  const dv2 = calcModulo11Dv([...base, dv1], [11, 10, 9, 8, 7, 6, 5, 4, 3, 2])
  return nums[9] === dv1 && nums[10] === dv2
}

function isValidCnpj(digits: string): boolean {
  if (/^(\d)\1{13}$/.test(digits)) return false
  const nums = digits.split('').map(Number)
  const base = nums.slice(0, 12)
  const dv1 = calcModulo11Dv(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
  const dv2 = calcModulo11Dv([...base, dv1], [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
  return nums[12] === dv1 && nums[13] === dv2
}

export function validateTaxId(type: string, value: string): TaxIdValidation {
  const def = TAX_ID_TYPES.find((t) => t.id === type)
  if (!def) {
    return { ok: false, reason: 'UNKNOWN_TYPE' }
  }

  const normalized = normalizeTaxId(type, value)

  if (type === 'br_cpf') {
    if (normalized.length !== 11) return { ok: false, reason: 'INVALID_LENGTH' }
    if (!isValidCpf(normalized)) return { ok: false, reason: 'INVALID_CHECKSUM' }
    return { ok: true, normalized }
  }

  if (type === 'br_cnpj') {
    if (normalized.length !== 14) return { ok: false, reason: 'INVALID_LENGTH' }
    if (!isValidCnpj(normalized)) return { ok: false, reason: 'INVALID_CHECKSUM' }
    return { ok: true, normalized }
  }

  if (def.format) {
    if (!new RegExp(def.format).test(normalized)) {
      return { ok: false, reason: 'INVALID_FORMAT' }
    }
    return { ok: true, normalized }
  }

  if (normalized.length === 0) {
    return { ok: false, reason: 'INVALID_FORMAT' }
  }

  return { ok: true, normalized }
}
